import {
  FormSectionSchema,
  IncidentFormTypeSchema,
  WorkflowVersionSchema,
  type Workflow,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";
import { buildRecordPolicePresenceFormSpecification } from "../common/recordPolicePresence/helpers.js";
import { colpAnonymiser } from "./anonymiser.js";
import {
  formSpecification as arrivePlaceOfSafetyFormSpecification,
  type ArrivePlaceOfSafetyFormData,
} from "./arrivePlaceOfSafety.js";
import {
  formSpecification as createIncidentFormSpecification,
  type CreateIncidentWorkItemFormData,
} from "./createIncident.js";
import {
  formSpecification as detentionDetailsFormSpecification,
  type DetentionDetailsFormData,
} from "./detentionDetails.js";
import {
  formSpecification as finishFormSpecification,
  type FinishFormData,
} from "./finishForm.js";
import {
  formSpecification as informationForHealthcareFormSpecification,
  type InformationForHealthcareFormData,
} from "./informationForHealthcare.js";
import {
  formSpecification as leavePlaceOfSafetyFormSpecification,
  type LeavePlaceOfSafetyFormData,
} from "./leavePlaceOfSafety.js";
import {
  formSpecification as mcaAccompanimentFormSpecification,
  type McaAccompanimentFormData,
} from "./mcaAccompaniment.js";
import {
  formSpecification as mcaCapacityAssessmentFormSpecification,
  type McaCapacityAssessmentFormData,
} from "./mcaCapacityAssessment.js";
import {
  formSpecification as mcaIncidentDetailsFormSpecification,
  type McaIncidentDetailsFormData,
} from "./mcaIncidentDetails.js";
import {
  formSpecification as mcaPersonDetailsFormSpecification,
  type McaPersonDetailsFormData,
} from "./mcaPersonDetails.js";
import {
  formSpecification as personDetailsFormSpecification,
  type PersonDetailsFormData,
} from "./personDetails.js";
import {
  formSpecification as preEndIncidentFormSpecification,
  type PreEndIncidentFormData,
} from "./preEndIncident.js";
import {
  formSpecification as recordPlaceOfSafetyFormSpecification,
  type RecordPlaceOfSafetyFormData,
} from "./recordPlaceOfSafety.js";
import {
  formSpecification as recordPolicePresenceFormSpecification,
  type RecordPolicePresenceFormData,
} from "./recordPolicePresence.js";
import { formSpecification as recordPolicePresenceOfficerResponseNoFormSpecification } from "./recordPolicePresenceOfficerResponseNo.js";
import { formSpecification as recordPolicePresenceOfficerResponseYesFormSpecification } from "./recordPolicePresenceOfficerResponseYes.js";
import {
  formSpecification as requestPolicePresenceMainFormSpecification,
  type RequestPolicePresenceMainFormData,
} from "./requestPolicePresenceMain.js";
import {
  formSpecification as s136FormRejectedFormSpecification,
  type S136FormRejectedFormData,
} from "./s136FormRejected.js";
import {
  formSpecification as shareIncidentFormSpecification,
  type ShareIncidentFormData,
} from "./shareIncident.js";
import {
  formSpecification as triageIncidentFormSpecification,
  type TriageIncidentFormData,
} from "./triageIncident.js";

export const workflow_COLP_1_0: Workflow<
  CreateIncidentWorkItemFormData,
  TriageIncidentFormData,
  PersonDetailsFormData,
  DetentionDetailsFormData,
  InformationForHealthcareFormData,
  McaPersonDetailsFormData,
  McaIncidentDetailsFormData,
  McaCapacityAssessmentFormData,
  McaAccompanimentFormData,
  RecordPlaceOfSafetyFormData,
  ArrivePlaceOfSafetyFormData,
  LeavePlaceOfSafetyFormData,
  PreEndIncidentFormData,
  RequestPolicePresenceMainFormData,
  RecordPolicePresenceFormData,
  FinishFormData,
  ShareIncidentFormData,
  S136FormRejectedFormData
> = {
  workflowVersion: WorkflowVersionSchema.enum.COLP_1_0,

  anonymiser: colpAnonymiser,

  [IncidentFormTypeSchema.enum.createIncident]: {
    formDefinition: {
      sections: [
        {
          id: FormSectionSchema.enum.createIncidentMain,
          name: tk("incidentWorkItem.startNew"),
          autoSave: false,
          buildFormSpecification: createIncidentFormSpecification,
        },
      ],
    },
  },

  [IncidentFormTypeSchema.enum.triage]: {
    formDefinition: {
      sections: [
        {
          id: FormSectionSchema.enum.triageMain,
          name: tk("triageIncident.title"),
          autoSave: true,
          buildFormSpecification: triageIncidentFormSpecification,
        },
      ],
    },
  },

  [IncidentFormTypeSchema.enum.shareIncident]: {
    formDefinition: {
      sections: [
        {
          id: FormSectionSchema.enum.shareIncident,
          name: tk("shareIncident.title"),
          autoSave: false,
          buildFormSpecification: shareIncidentFormSpecification,
        },
      ],
    },
  },

  [IncidentFormTypeSchema.enum.recordPlaceOfSafety]: {
    formDefinition: {
      sections: [
        {
          id: FormSectionSchema.enum.recordPlaceOfSafetyMain,
          name: tk("recordPlaceOfSafety.title"),
          autoSave: true,
          buildFormSpecification: recordPlaceOfSafetyFormSpecification,
        },
        {
          id: FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          name: tk("arrivePlaceOfSafety.title"),
          autoSave: true,
          buildFormSpecification: arrivePlaceOfSafetyFormSpecification,
        },
        {
          id: FormSectionSchema.enum.recordPlaceOfSafetyLeave,
          name: tk("leavePlaceOfSafety.title"),
          autoSave: true,
          buildFormSpecification: leavePlaceOfSafetyFormSpecification,
        },
      ],
    },
  },

  [IncidentFormTypeSchema.enum.requestPolicePresence]: {
    formDefinition: {
      sections: [
        {
          id: FormSectionSchema.enum.requestPolicePresenceMain,
          name: tk("requestPolicePresence.title"),
          autoSave: false,
          buildFormSpecification: requestPolicePresenceMainFormSpecification,
        },
        {
          id: FormSectionSchema.enum.recordPolicePresence,
          name: tk("recordPolicePresence.title"),
          autoSave: false,
          buildFormSpecification:
            buildRecordPolicePresenceFormSpecification<RecordPolicePresenceFormData>(
              recordPolicePresenceFormSpecification,
              recordPolicePresenceOfficerResponseNoFormSpecification,
              recordPolicePresenceOfficerResponseYesFormSpecification,
            ),
        },
      ],
    },
  },

  [IncidentFormTypeSchema.enum.preEndIncident]: {
    formDefinition: {
      sections: [
        {
          id: FormSectionSchema.enum.preEndIncident,
          name: tk("incidentFormSections.endForm"),
          autoSave: false,
          buildFormSpecification: preEndIncidentFormSpecification,
        },
      ],
    },
  },

  [IncidentFormTypeSchema.enum.finishForm]: {
    formDefinition: {
      sections: [
        {
          id: FormSectionSchema.enum.finishForm,
          name: tk("finishForm.finishFormTitle"),
          autoSave: false,
          buildFormSpecification: finishFormSpecification,
        },
      ],
    },
  },

  [IncidentFormTypeSchema.enum.section136]: {
    formDefinition: {
      sections: [
        {
          id: FormSectionSchema.enum.personDetails,
          name: tk("incidentFormSections.person"),
          autoSave: true,
          buildFormSpecification: personDetailsFormSpecification,
        },
        {
          id: FormSectionSchema.enum.detentionDetails,
          name: tk("incidentFormSections.detention"),
          autoSave: true,
          buildFormSpecification: detentionDetailsFormSpecification,
        },
        {
          id: FormSectionSchema.enum.informationForHealthcare,
          name: tk("incidentFormSections.information"),
          autoSave: true,
          buildFormSpecification: informationForHealthcareFormSpecification,
        },
      ],
    },
  },

  [IncidentFormTypeSchema.enum.s136FormRejected]: {
    formDefinition: {
      sections: [
        {
          id: FormSectionSchema.enum.s136FormRejected,
          name: tk("s136FormRejected.title"),
          autoSave: false,
          buildFormSpecification: s136FormRejectedFormSpecification,
        },
      ],
    },
  },

  [IncidentFormTypeSchema.enum.mentalCapacityAct]: {
    formDefinition: {
      sections: [
        {
          id: FormSectionSchema.enum.mcaPersonDetails,
          name: tk("incidentFormSections.mcaPersonDetails"),
          autoSave: true,
          buildFormSpecification: mcaPersonDetailsFormSpecification,
        },
        {
          id: FormSectionSchema.enum.mcaIncidentDetails,
          name: tk("incidentFormSections.mcaIncidentDetails"),
          autoSave: true,
          buildFormSpecification: mcaIncidentDetailsFormSpecification,
        },
        {
          id: FormSectionSchema.enum.mcaCapacityAssessment,
          name: tk("incidentFormSections.mcaCapacityAssessment"),
          autoSave: true,
          buildFormSpecification: mcaCapacityAssessmentFormSpecification,
        },
        {
          id: FormSectionSchema.enum.mcaAccompaniment,
          name: tk("incidentFormSections.mcaAccompaniment"),
          autoSave: true,
          buildFormSpecification: mcaAccompanimentFormSpecification,
        },
      ],
    },
  },
};
